import { useMemo, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useGfsOfferConfigGetQuery,
  useGfsOfferConfigPutMutation,
  useLazyGfsOfferConfigPreviewPdfQuery,
  updateGfsOfferConfigCache,
} from "@/services/backend/gfs/offer/api";
import {
  GfsOfferConfig,
  PutGfsOfferConfigRequest,
} from "@/services/backend/gfs/offer/types";
import { GfsOfferConfigEditor } from "@/services/backend/gfs/offer/components/offer-config-editor";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import type { RtkQueryMutation } from "@/shared/lib/debounce/debounce";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { DocumentSheetPreview } from "@/shared/bsm/core/document/components/ui/document-sheet";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export function GfsOfferConfigPage() {
  const { companyId } = useActiveCompany();
  const { data, error } = useGfsOfferConfigGetQuery({ companyId });
  if (error) return <RTKQueryErrorAlert error={error} />;
  if (!data) return <Loader2 className="animate-spin" />;
  return (
    <PersistenceContextProvider>
      <GfsOfferConfigAutosave config={data} companyId={companyId} />
    </PersistenceContextProvider>
  );
}
function GfsOfferConfigAutosave({
  config,
  companyId,
}: {
  config: GfsOfferConfig;
  companyId: string;
}) {
  const initialRequest = useMemo(
    () => ({ companyId, config }),
    [companyId, config],
  );
  const [put, state] = useGfsOfferConfigPutMutation();
  const typed = [put, state] as unknown as RtkQueryMutation<
    PutGfsOfferConfigRequest,
    GfsOfferConfig
  >;
  const [setConfig] = useOptimisticDebouncedMutation<
    GfsOfferConfig,
    PutGfsOfferConfigRequest,
    GfsOfferConfig
  >(
    initialRequest,
    (nextConfig) => updateGfsOfferConfigCache(companyId, nextConfig),
    typed,
    {
      delay: 500,
      toastError: true,
    },
  );
  const [open, setOpen] = useState(false);
  const [loadPreview, preview] = useLazyGfsOfferConfigPreviewPdfQuery();
  const openPreview = async () => {
    try {
      await loadPreview({ companyId }).unwrap();
      setOpen(true);
    } catch {
      setOpen(false);
    }
  };
  return (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-muted-foreground text-sm">
          Die Bereiche sind vorbereitet. Änderungen werden automatisch
          gespeichert und gelten nur für neu erstellte Angebote.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={openPreview}
          disabled={preview.isFetching}
        >
          <FileText className="mr-1 h-4 w-4" />
          PDF Vorschau
        </Button>
      </div>
      <GfsOfferConfigEditor
        config={config}
        onChange={(nextConfig) =>
          setConfig({ companyId, config: nextConfig }, nextConfig)
        }
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-screen max-w-screen rounded-none sm:w-[96vw] sm:max-w-[96vw] sm:rounded-lg lg:w-[70vw] lg:max-w-[231mm]">
          <DialogHeader>
            <DialogTitle>Angebotstexte Vorschau</DialogTitle>
          </DialogHeader>
          <DocumentSheetPreview
            objectURL={preview.data?.objectURL}
            isLoading={preview.isLoading || preview.isFetching}
            error={preview.error}
            className="!md:col-span-1 w-full"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
